/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  GUEST_CHIP_COLOR,
  GUEST_CHIP_LABEL,
  STATUS_HEX,
  STATUS_LABELS,
  TEAL,
  TEAL_BORDER,
  TEAL_LIGHT,
  formatAppointmentNo,
  formatDisplayDate,
  formatDisplayTime,
  getQueueSynopsis,
  getTodayDateStr,
  groupAppointmentsByDoctor,
  resolveDoctorName,
  resolvePatientMobile,
  resolvePatientName,
} from '../constants';
import DoctorQueueSynopsis from './DoctorQueueSynopsis';
import QuickActionIcons from './QuickActionIcons';

// Same compact "Paper, left status border, chip + name + phone inline, muted
// second line" language as layouts/followUps/index.js's FollowupRow.
function AppointmentRow({ appointment, onSelect, onChanged, isInQueue, isNextQueue }) {
  const statusColor = STATUS_HEX[appointment.status] || '#90a4ae';
  const patientName = resolvePatientName(appointment, appointment.irm_contacts_id);
  const phone = resolvePatientMobile(appointment);

  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(appointment.id)}
      sx={{
        mb: 0.75,
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        cursor: 'pointer',
        outline: isInQueue ? `2px solid ${statusColor}` : isNextQueue ? '2px dashed #90a4ae' : 'none',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': { boxShadow: '0 1px 8px rgba(0,121,142,0.1)', borderColor: statusColor },
      }}
    >
      <Box sx={{ px: 1.75, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            <Chip
              label={STATUS_LABELS[appointment.status] || appointment.status}
              size="small"
              sx={{
                height: 17, fontSize: '0.6rem', fontWeight: 700,
                bgcolor: `${statusColor}18`, color: statusColor,
                border: `1px solid ${statusColor}44`,
                '& .MuiChip-label': { px: 0.7 },
              }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: statusColor, fontSize: '0.68rem' }}>
              {formatAppointmentNo(appointment)}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.83rem', color: '#222' }}>
              {patientName}
            </Typography>
            {appointment.is_guest === 'Y' && (
              <Chip label={GUEST_CHIP_LABEL} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: GUEST_CHIP_COLOR, color: '#fff', '& .MuiChip-label': { px: 0.7 } }} />
            )}
            {phone && (
              <Box display="flex" alignItems="center" gap={0.25}>
                <PhoneIcon sx={{ fontSize: 10, color: '#ccc' }} />
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.68rem' }}>{phone}</Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={0.3}>
              <AccessTimeIcon sx={{ fontSize: 10, color: TEAL }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.68rem' }}>
                {formatDisplayDate(appointment.appointment_date)}
                {appointment.appointment_time && ` · ${formatDisplayTime(appointment.appointment_time)}`}
              </Typography>
            </Box>
          </Box>

          {appointment.reason_for_visit && (
            <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.68rem', lineHeight: 1.5, mt: 0.35, pl: 0.25 }}>
              {appointment.reason_for_visit}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label={`# ${appointment.serial_no}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', borderRadius: 4 }} />
            {isInQueue && <Chip label="IN" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: statusColor, color: '#fff !important', fontWeight: 700, borderRadius: 4 }} />}
            {isNextQueue && <Chip label="NEXT" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, borderRadius: 4 }} />}
          </Box>
          <QuickActionIcons appointment={appointment} onChanged={onChanged} />
        </Box>
      </Box>
    </Paper>
  );
}

export default function AppointmentListView({ appointments, onSelect, onChanged }) {
  const doctorGroups = useMemo(() => groupAppointmentsByDoctor(appointments), [appointments]);
  const today = getTodayDateStr();

  if (appointments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <EventBusyIcon sx={{ fontSize: 44, color: '#e0e0e0', mb: 1 }} />
        <Typography variant="body2" color="textSecondary">
          No appointments found for the selected filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {doctorGroups.map(group => {
        const todaysForDoctor = group.appointments.filter(a => a.appointment_date === today);
        const { inAppointment, nextAppointment } = getQueueSynopsis(todaysForDoctor);

        return (
          <Box key={group.doctorId} sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 1.5,
                py: 1,
                mb: 1,
                bgcolor: TEAL_LIGHT,
                borderRadius: 2,
                border: `1px solid ${TEAL_BORDER}`,
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75}>
                <LocalHospitalIcon sx={{ color: TEAL, fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, color: TEAL, fontSize: '0.85rem' }}>
                  {resolveDoctorName(group.doctorInfo, group.doctorId)}
                </Typography>
                <Chip
                  label={group.appointments.length}
                  size="small"
                  sx={{ height: 18, fontSize: '0.62rem', bgcolor: TEAL, color: '#fff !important' }}
                />
              </Box>
              <DoctorQueueSynopsis inAppointment={inAppointment} nextAppointment={nextAppointment} />
            </Box>

            {group.appointments.map(appointment => (
              <AppointmentRow
                key={appointment.id}
                appointment={appointment}
                onSelect={onSelect}
                onChanged={onChanged}
                isInQueue={inAppointment?.id === appointment.id}
                isNextQueue={nextAppointment?.id === appointment.id}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
